import { getLocalItem, getSessionItem, setLocalItem, setSessionItem } from '../utils/safeStorage';

const resolveApiBase = () => {
  const envBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    '';
  if (typeof window === 'undefined') return envBase || '';
  if (!envBase) return '';
  if (process.env.NODE_ENV !== 'production' && envBase.replace(/\/$/, '') === window.location.origin.replace(/\/$/, '')) {
    return '';
  }
  return envBase;
};

const API_BASE = resolveApiBase();
const ADMIN_ACCESS_KEY = 'adminAccessToken';
const LEGACY_ADMIN_ACCESS_KEY = 'hv_admin_token';
const HOST_ACCESS_KEY = 'hostAccessToken';
const PROPERTY_CACHE = new Map();
const RECENT_CACHE = [];
const RECENT_LIMIT = 12;

const parseUrl = (path) => {
  if (!path || typeof path !== 'string') return null;
  if (!path.startsWith('/')) return null;
  try {
    return new URL(path, 'https://placeholder.invalid');
  } catch {
    return null;
  }
};

const ADMIN_PROPERTIES_PATH = '/api/admin/properties';
const ADMIN_PROPERTIES_QUERY_ALLOWLIST = new Set([
  'page',
  'limit',
  'search',
  'q',
  'id',
  'propertyId',
  'slug',
  'status',
  'city',
  'type',
  'source',
  'host',
  'minPrice',
  'maxPrice',
  'rating',
  'minNights',
  'featured',
  'apiEnabled',
  'searchable',
  'showOnHomepage',
  'availableFrom',
  'availableTo',
]);

const sanitizeNumberish = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const cleaned = raw.replace(/[^\d.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return '';
  const num = Number(cleaned);
  if (!Number.isFinite(num)) return '';
  return String(num);
};

const sanitizeBoolish = (value) => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return '';
  if (['true', '1', 'yes', 'on'].includes(raw)) return 'true';
  if (['false', '0', 'no', 'off'].includes(raw)) return 'false';
  return '';
};

const sanitizeAdminPropertiesPath = (path) => {
  const url = parseUrl(path);
  if (!url || !url.pathname.startsWith(ADMIN_PROPERTIES_PATH)) return null;

  const next = new URLSearchParams();
  url.searchParams.forEach((value, key) => {
    if (!ADMIN_PROPERTIES_QUERY_ALLOWLIST.has(key)) return;
    const raw = String(value ?? '').trim();
    if (!raw) return;

    if (['page', 'limit', 'minPrice', 'maxPrice', 'rating', 'minNights'].includes(key)) {
      const safe = sanitizeNumberish(raw);
      if (!safe) return;
      next.set(key, safe);
      return;
    }

    if (['featured', 'apiEnabled', 'searchable', 'showOnHomepage'].includes(key)) {
      const safe = sanitizeBoolish(raw);
      if (!safe) return;
      next.set(key, safe);
      return;
    }

    if (key === 'city') {
      const safe = raw
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)
        .slice(0, 8)
        .join(',');
      if (!safe) return;
      next.set(key, safe);
      return;
    }

    next.set(key, raw);
  });

  return `${url.pathname}${next.toString() ? `?${next.toString()}` : ''}`;
};

const normalizeSearchParams = (params) => {
  const location = params.get('location') || params.get('destination') || params.get('search');
  if (location && !params.get('city')) params.set('city', location);
  const search = params.get('search');
  if (search && !params.get('q')) params.set('q', search);
  const lowerCheckIn = params.get('checkin');
  const lowerCheckOut = params.get('checkout');
  if (lowerCheckIn && !params.get('checkIn')) params.set('checkIn', lowerCheckIn);
  if (lowerCheckOut && !params.get('checkOut')) params.set('checkOut', lowerCheckOut);
  params.delete('checkin');
  params.delete('checkout');
  const limit = Number(params.get('limit'));
  const offset = Number(params.get('offset'));
  if (Number.isFinite(limit) && limit > 0 && Number.isFinite(offset) && offset >= 0 && !params.get('page')) {
    params.set('page', String(Math.floor(offset / limit) + 1));
  }
};

const mapPublicPropertiesPathToLegacy = (url) => {
  if (!url || !url.pathname.startsWith('/api/public/properties')) return;
  const rest = url.pathname.slice('/api/public/properties'.length);
  if (!rest || rest === '/') {
    url.pathname = '/api/properties';
  } else if (rest === '/search') {
    url.pathname = '/api/properties';
    normalizeSearchParams(url.searchParams);
  } else if (rest === '/featured') {
    url.pathname = '/api/properties';
    url.searchParams.set('featured', 'true');
  } else if (rest === '/trending') {
    url.pathname = '/api/properties';
    url.searchParams.set('trending', 'true');
  } else if (rest === '/luxury') {
    url.pathname = '/api/properties';
    const tags = url.searchParams.get('tags');
    url.searchParams.set('tags', tags ? `${tags},luxury` : 'luxury');
  } else {
    url.pathname = `/api/properties${rest}`;
  }
};

const normalizePath = (path) => {
  const url = parseUrl(path);
  if (!url) return { path, pathname: path };

  if (url.pathname === '/api/properties/search') {
    url.pathname = '/api/properties';
    normalizeSearchParams(url.searchParams);
  }

  if (url.pathname === '/api/public/properties/search') {
    normalizeSearchParams(url.searchParams);
  }

  if (url.pathname.startsWith('/api/public/properties')) {
    normalizeSearchParams(url.searchParams);
  }

  return { path: `${url.pathname}${url.search}`, pathname: url.pathname };
};

const getLegacyPropertyFallback = (path) => {
  const url = parseUrl(path);
  if (!url) return null;
  if (!url.pathname.startsWith('/api/public/properties')) return null;
  mapPublicPropertiesPathToLegacy(url);
  return { path: `${url.pathname}${url.search}`, pathname: url.pathname };
};

const RESERVED_PROPERTY_PATHS = new Set(['search', 'nearby', 'featured', 'trending', 'luxury']);

const isPropertyDetailPath = (pathname) => {
  if (!pathname || typeof pathname !== 'string') return false;
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length !== 3) return false;
  if (segments[0] !== 'api' || segments[1] !== 'properties') return false;
  return !RESERVED_PROPERTY_PATHS.has(segments[2]);
};

const normalizePropertyRecord = (record) => {
  if (!record || typeof record !== 'object') return record;
  if (!record.id && record._id) record.id = record._id;
  if (!record.id && record.property_id) record.id = record.property_id;
  return record;
};

const cacheProperty = (record) => {
  if (!record || typeof record !== 'object') return;
  const normalized = normalizePropertyRecord(record);
  const keys = [normalized.id, normalized._id, normalized.slug, normalized.property_id]
    .filter(Boolean)
    .map((value) => String(value));
  keys.forEach((key) => {
    PROPERTY_CACHE.set(key, normalized);
  });
};

const cachePayload = (payload) => {
  if (!payload) return;
  if (Array.isArray(payload)) {
    payload.forEach(cacheProperty);
    return;
  }
  if (Array.isArray(payload.data)) {
    payload.data.forEach(cacheProperty);
    return;
  }
  if (Array.isArray(payload.items)) {
    payload.items.forEach(cacheProperty);
    return;
  }
  if (payload && typeof payload === 'object' && (payload.slug || payload.id || payload._id)) {
    cacheProperty(payload);
  }
};

const adaptResponse = (pathname, payload) => {
  if (!payload) return payload;
  let data = payload;
  if (isPropertyDetailPath(pathname) && payload && typeof payload === 'object' && payload.data) {
    data = payload.data;
  }
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    normalizePropertyRecord(data);
  }
  cachePayload(data);
  return data;
};

const parseBody = (body) => {
  if (!body) return null;
  if (typeof body === 'string') {
    try {
      return JSON.parse(body);
    } catch {
      return null;
    }
  }
  return body;
};

const calcNights = (checkIn, checkOut) => {
  try {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;
    const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return Math.max(1, diff);
  } catch {
    return 1;
  }
};

const getPropertyPrice = (property) => {
  if (!property) return 0;
  const value =
    property.sell_price_per_night ||
    property.sellPricePerNight ||
    property.final_per_night_display_price ||
    property.price_per_night ||
    property.base_price_per_night ||
    property.pricePerNight ||
    property.basePricePerNight ||
    property.price ||
    property.base_price ||
    0;
  return Number(value) || 0;
};

const buildQuoteFallback = (body) => {
  const payload = parseBody(body) || {};
  const stayId = payload.property_id || payload.propertyId || payload.stayId;
  const property = stayId ? PROPERTY_CACHE.get(String(stayId)) : null;
  const nights = calcNights(payload.check_in, payload.check_out);
  const base = getPropertyPrice(property) * nights;
  return {
    base_price: base,
    discount_amount: 0,
    taxable_subtotal: base,
    gst_amount: 0,
    mandatory_fees: 0,
    final_payable: base,
    base_nights_amount: base,
    fees_total: 0,
    taxes_total: 0,
    total_discount: 0,
    total_payable: base,
    nights,
    currency: property?.currency || property?.base_currency || 'INR',
    eligible_promotions: [],
    eligible_card_offers: [],
    discounts: [],
  };
};

const trackRecentFallback = (body) => {
  const payload = parseBody(body) || {};
  const propertyId = payload.propertyId || payload.property_id;
  if (!propertyId) return { ok: true };
  const key = String(propertyId);
  const cached = PROPERTY_CACHE.get(key);
  const entry = cached ? { ...cached } : { id: propertyId };
  const existingIndex = RECENT_CACHE.findIndex(
    (item) => String(item?.id || item?._id || item?.property_id) === key
  );
  if (existingIndex >= 0) RECENT_CACHE.splice(existingIndex, 1);
  RECENT_CACHE.unshift(entry);
  if (RECENT_CACHE.length > RECENT_LIMIT) RECENT_CACHE.length = RECENT_LIMIT;
  return { ok: true };
};

const buildFallbackResponse = (method, pathname, body) => {
  if (pathname === '/api/recently-viewed') {
    return method === 'GET' ? [...RECENT_CACHE] : trackRecentFallback(body);
  }
  if (pathname === '/api/properties/nearby') {
    return { items: [] };
  }
  if (pathname.includes('/availability/quote')) {
    return buildQuoteFallback(body);
  }
  if (pathname.includes('/availability')) {
    return { days: [], property: {} };
  }
  if (pathname === '/api/public/pricing/quote' || pathname === '/api/pricing/quote') {
    return buildQuoteFallback(body);
  }
  if (pathname === '/api/public/coupons') {
    return [];
  }
  if (pathname === '/api/public/promo/validate') {
    return { valid: false };
  }
  return null;
};

const getHeaders = (token) => ({
  'Content-Type': 'application/json',
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

export const safeStringify = (value) => {
  const seen = new WeakSet();
  const replacer = (_, val) => {
    if (val === null || val === undefined) return val;
    const t = typeof val;
    if (t === 'function' || t === 'symbol') return undefined;
    if (t === 'bigint') return val.toString();
    if (t !== 'object') return val;

    const ctor = val.constructor && val.constructor.name;
    // Drop timers and infra objects that JSON can't handle
    if (ctor === 'Timeout' || ctor === 'Timer' || ctor === 'TimersList') return undefined;
    // Guard against internal timer fields that might sneak through
    if (val._idlePrev || val._idleNext || val._onTimeout) return undefined;
    if (seen.has(val)) return undefined;
    seen.add(val);

    if (val instanceof Date) return val.toISOString();
    if (val instanceof Set) return Array.from(val);
    if (val instanceof Map) return Object.fromEntries(val);
    return val;
  };

  try {
    return JSON.stringify(value, replacer);
  } catch (_) {
    // Fall back to a minimal payload so we never throw on circular refs
    return JSON.stringify(null);
  }
};

const persistAdminToken = (token) => {
  if (!token) return;
  setSessionItem(ADMIN_ACCESS_KEY, token);
  setLocalItem(ADMIN_ACCESS_KEY, token);
  setSessionItem(LEGACY_ADMIN_ACCESS_KEY, token);
  setLocalItem(LEGACY_ADMIN_ACCESS_KEY, token);
};

const maybeRefreshAdminToken = async (token, path = '') => {
  const isAdminPath =
    path.startsWith('/api/admin') || path.startsWith('/api/roles') || path.startsWith('/api/permissions');
  if (!isAdminPath) return null;

  try {
    const res = await fetch(`${API_BASE}/api/admin/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = await res.json().catch(() => ({}));
    const nextToken = data?.accessToken || data?.token || '';
    if (nextToken) {
      persistAdminToken(nextToken);
      return nextToken;
    }
    return null;
  } catch {
    return null;
  }
};

const maybeRefreshHostToken = async (token, path) => {
  if (!token) return null;
  if (!path.startsWith('/api/host') && !path.startsWith('/api/auth/host')) return null;
  const stored = getSessionItem(HOST_ACCESS_KEY) || getLocalItem(HOST_ACCESS_KEY);
  if (stored && stored !== token) return null;
  try {
    const res = await fetch(`${API_BASE}/api/auth/host/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = await res.json();
    const newToken = data.accessToken || data.token;
    if (newToken) {
      setSessionItem(HOST_ACCESS_KEY, newToken);
      setLocalItem(HOST_ACCESS_KEY, newToken);
    }
    return newToken || null;
  } catch {
    return null;
  }
};

const handle = async (res, retry) => {
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    let parsed = null;
    try {
      const data = JSON.parse(text);
      parsed = data;
      msg =
        data?.message ||
        data?.error?.message ||
        data?.error ||
        data?.detail ||
        text;
    } catch (_) {
      // keep text
    }
    const rawDetail = parsed?.detail;
    if (typeof rawDetail === 'string' && rawDetail.trim().toLowerCase() === 'bad request') {
      const fieldErrors = parsed?.errors || parsed?.error?.details || null;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const first = Object.values(fieldErrors).find(Boolean);
        if (first) msg = String(first);
      } else if (!msg || msg.trim().toLowerCase() === 'bad request') {
        msg = 'Request validation failed. Please review your input and try again.';
      }
    }
    if (typeof msg === 'string' && msg.trim().toLowerCase() === 'bad request') {
      msg = 'Request validation failed. Please review your input and try again.';
    }
    const error = new Error(msg || 'Request failed');
    error.status = res.status;
    error.retry = retry;
    error.errors = parsed?.errors || parsed?.error?.details || null;
    throw error;
  }
  return text ? JSON.parse(text) : {};
};

const handleText = async (res, retry) => {
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    let parsed = null;
    try {
      const data = JSON.parse(text);
      parsed = data;
      msg =
        data?.message ||
        data?.error?.message ||
        data?.error ||
        data?.detail ||
        text;
    } catch (_) {
      // keep text
    }
    const rawDetail = parsed?.detail;
    if (typeof rawDetail === 'string' && rawDetail.trim().toLowerCase() === 'bad request') {
      msg = 'Request validation failed. Please review your input and try again.';
    }
    if (typeof msg === 'string' && msg.trim().toLowerCase() === 'bad request') {
      msg = 'Request validation failed. Please review your input and try again.';
    }
    const error = new Error(msg || 'Request failed');
    error.status = res.status;
    error.retry = retry;
    error.errors = parsed?.errors || parsed?.error?.details || null;
    throw error;
  }
  return text || '';
};

const resolveToken = (path, token) => {
  if (token) return token;
  if (path.startsWith('/api/admin') || path.startsWith('/api/roles') || path.startsWith('/api/permissions')) {
    return (
      getSessionItem(ADMIN_ACCESS_KEY) ||
      getLocalItem(ADMIN_ACCESS_KEY) ||
      getSessionItem(LEGACY_ADMIN_ACCESS_KEY) ||
      getLocalItem(LEGACY_ADMIN_ACCESS_KEY) ||
      ''
    );
  }
  if (path.startsWith('/api/host')) {
    return getSessionItem(HOST_ACCESS_KEY) || getLocalItem(HOST_ACCESS_KEY) || '';
  }
  return '';
};

const request = async (method, path, body, token) => {
  const buildBody = () => {
    if (body === undefined) return undefined;
    const serialized = safeStringify(body);
    return serialized;
  };

  const { path: normalizedPath, pathname } = normalizePath(path);
  const resolvedToken = resolveToken(normalizedPath, token);
  const doFetch = (targetPath, authToken) =>
    fetch(`${API_BASE}${targetPath}`, {
      method,
      headers: getHeaders(authToken),
      credentials: 'include',
      ...(body !== undefined ? { body: buildBody() } : {}),
    });

  let res = await doFetch(normalizedPath, resolvedToken);
  try {
    const payload = await handle(res);
    return adaptResponse(pathname, payload);
  } catch (err) {
    if (method === 'GET' && err.status === 400 && normalizedPath.startsWith(ADMIN_PROPERTIES_PATH)) {
      const safePath = sanitizeAdminPropertiesPath(normalizedPath);
      if (safePath && safePath !== normalizedPath) {
        res = await doFetch(safePath, resolvedToken);
        const payload = await handle(res);
        return adaptResponse(pathname, payload);
      }
    }
    if (err.status === 401) {
      const refreshed = await maybeRefreshAdminToken(resolvedToken, normalizedPath);
      if (refreshed) {
        res = await doFetch(normalizedPath, refreshed);
        const payload = await handle(res);
        return adaptResponse(pathname, payload);
      }
      const hostRefreshed = await maybeRefreshHostToken(resolvedToken, normalizedPath);
      if (hostRefreshed) {
        res = await doFetch(normalizedPath, hostRefreshed);
        const payload = await handle(res);
        return adaptResponse(pathname, payload);
      }
    }
    if (err.status === 404) {
      const legacyFallback = getLegacyPropertyFallback(normalizedPath);
      if (legacyFallback && legacyFallback.path !== normalizedPath) {
        res = await doFetch(legacyFallback.path, resolvedToken);
        const payload = await handle(res);
        return adaptResponse(legacyFallback.pathname, payload);
      }
    }
    const fallback = buildFallbackResponse(method, pathname, body);
    if (fallback !== null && fallback !== undefined) return fallback;
    throw err;
  }
};

const requestText = async (method, path, body, token) => {
  const buildBody = () => {
    if (body === undefined) return undefined;
    const serialized = safeStringify(body);
    return serialized;
  };

  const { path: normalizedPath, pathname } = normalizePath(path);
  const resolvedToken = resolveToken(normalizedPath, token);
  const doFetch = (targetPath, authToken) =>
    fetch(`${API_BASE}${targetPath}`, {
      method,
      headers: getHeaders(authToken),
      credentials: 'include',
      ...(body !== undefined ? { body: buildBody() } : {}),
    });

  let res = await doFetch(normalizedPath, resolvedToken);
  try {
    return await handleText(res);
  } catch (err) {
    if (err.status === 401) {
      const refreshed = await maybeRefreshAdminToken(resolvedToken, normalizedPath);
      if (refreshed) {
        res = await doFetch(normalizedPath, refreshed);
        return await handleText(res);
      }
      const hostRefreshed = await maybeRefreshHostToken(resolvedToken, normalizedPath);
      if (hostRefreshed) {
        res = await doFetch(normalizedPath, hostRefreshed);
        return await handleText(res);
      }
    }
    if (err.status === 404) {
      const legacyFallback = getLegacyPropertyFallback(normalizedPath);
      if (legacyFallback && legacyFallback.path !== normalizedPath) {
        res = await doFetch(legacyFallback.path, resolvedToken);
        return await handleText(res);
      }
    }
    const fallback = buildFallbackResponse(method, pathname, body);
    if (fallback !== null && fallback !== undefined) {
      return typeof fallback === 'string' ? fallback : safeStringify(fallback);
    }
    throw err;
  }
};

export const apiGet = (path, token) => request('GET', path, undefined, token);
export const apiPost = (path, body, token) => request('POST', path, body, token);
export const apiPut = (path, body, token) => request('PUT', path, body, token);
export const apiPatch = (path, body, token) => request('PATCH', path, body, token);
export const apiDelete = (path, token) => request('DELETE', path, undefined, token);
export const apiGetText = (path, token) => requestText('GET', path, undefined, token);


